import { LoadingOutlined } from "@ant-design/icons";
import { Select, Spin } from "antd";
import axios, { CancelTokenSource } from "axios";
import React, { ReactNode, useEffect, useState } from "react";
import { LatLng } from "use-places-autocomplete";
import { EAC_COUNTRIES } from "../../../config/constants";
import Input from "../Input/input";

const { Option } = Select;
let cancelTokenSource: CancelTokenSource | null = null;

const antIcon = (
  <LoadingOutlined style={{ fontSize: 16, color: "black" }} spin />
);

interface ResultProps {
  boundingbox: string[];
  class: string;
  display_name: string;
  icon: string;
  importance: number;
  lat: string;
  licence: string;
  lon: string;
  osm_id: number;
  osm_type: string;
  place_id: number;
  type: string;
}

type StopTypes = {
  name: string;
  coordinates: LatLng;
};

interface Props {
  label?: string;
  name: string;
  placeholder?: string;
  rules?: any;
  prefixIcon?: ReactNode;
  setSelectedLocation: React.Dispatch<
    React.SetStateAction<StopTypes | undefined>
  >;
  height?: string;
}

const OSMInput: React.FC<Props> = ({
  label,
  rules,
  name,
  prefixIcon,
  placeholder = "Search location",
  setSelectedLocation = () => undefined,
  height
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [locations, setLocations] = useState<ResultProps[] | undefined>(
    undefined
  );

  const fetchLocations = async (query: string) => {
    try {
      cancelTokenSource = axios.CancelToken.source();

      setIsLoading(true);
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/search?q=${query}&format=jsonv2&countryCodes=${EAC_COUNTRIES.map(
          (country) => country.key
        ).join(",")}`,
        { cancelToken: cancelTokenSource.token }
      );

      if (res) {
        setLocations(res?.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (searchValue) {
        fetchLocations(searchValue);
      } else {
        setIsLoading(false);
      }
    }, 1000);

    return () => {
      if (cancelTokenSource !== null) {
        cancelTokenSource.cancel("Request canceled");
      }

      clearTimeout(timeOut);
    };
  }, [searchValue]);

  const onKeyUp = (val: string) => {
    setIsLoading(true);
    setSearchValue(val);
  };

  const onSelect = (val: string) => {
    if (val) {
      const parsedLocation: ResultProps = JSON.parse(val);
      setSelectedLocation({
        name: parsedLocation?.display_name,
        coordinates: { lat: +parsedLocation?.lat, lng: +parsedLocation?.lon }
      });
    }
  };

  return (
    <Input
      name={name}
      type="select"
      label={label}
      placeholder={placeholder}
      onKeyUp={onKeyUp}
      onChange={(e: string) => onSelect(e)}
      isGroupDropdown
      rules={rules}
      isLoading={isLoading}
      prefixIcon={prefixIcon}
      allowClear
      height={height}
      notFoundContent={
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 0"
          }}
        >
          {isLoading ? (
            <Spin indicator={antIcon} />
          ) : (
            <span>Type to search</span>
          )}
        </div>
      }
    >
      {locations?.map((location) => {
        return (
          <Option
            value={JSON.stringify(location)}
            key={location.display_name}
            title={location.display_name}
            onClick={(e: string) => onSelect(e)}
          >
            {location.display_name}
          </Option>
        );
      })}
    </Input>
  );
};

export default OSMInput;
